import { c as _c } from "react/compiler-runtime";
import { MessageCircle, Paperclip, Phone } from 'lucide-react';
import type { BlocoDocumento } from '@/shared/components/documentos/paginado';
import { OutboundStatusIcon } from '@/shared/components/ui/common/OutboundStatusIcon';
import type { ConversaDoCertificado, FatiaConversaCertificadoProps, FatiaTabelaCertificadoProps, TabelaCertificadoInput } from '../../../types/certificadoNegociacaoTypes';
import { dividirTextoCertificado, FATIA_DO_BALAO } from '../../../utils/certificadoNegociacao';
import { formatTelefoneComCodigoPais } from '@/shared/utils/contato';
import { dataDoMarco, horaDoMarco } from '../../../utils/marcoDataHora';
function FatiaTabela(t0) {
  const $ = _c(16);
  const {
    cabecalhos,
    colunas,
    linhas,
    indice,
    primeiroDaPagina
  } = t0;
  const abre = indice === 0 || primeiroDaPagina;
  const ultima = indice === linhas.length - 1;
  const t1 = `px-[37px] ${ultima ? "pb-[26px]" : ""}`;
  const t2 = `overflow-hidden border-x border-solid border-neutral-200 ${abre ? "rounded-t-[8px] border-t" : ""} ${ultima ? "rounded-b-[8px] border-b" : ""}`;
  let t3;
  if ($[0] !== abre || $[1] !== cabecalhos || $[2] !== colunas) {
    t3 = abre && <div className={`grid ${colunas} border-b border-neutral-200 bg-neutral-100 px-[14px] py-[9px] text-[11px] font-semibold tracking-[0.5px] text-neutral-500 uppercase`}>{cabecalhos.map(_temp)}</div>;
    $[0] = abre;
    $[1] = cabecalhos;
    $[2] = colunas;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = `grid ${colunas} items-start gap-x-3 px-[14px] py-[12px] ${ultima ? "" : "border-b border-neutral-100"}`;
  const t5 = linhas[indice];
  let t6;
  if ($[4] !== t5.celulas) {
    t6 = t5.celulas.map(_temp2);
    $[4] = t5.celulas;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t4 || $[7] !== t6) {
    t7 = <div className={t4}>{t6}</div>;
    $[6] = t4;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t7) {
    t8 = <div className={t2}>{t3}{t7}</div>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t1 || $[14] !== t8) {
    t9 = <div className={t1}>{t8}</div>;
    $[13] = t1;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}
function _temp2(celula, index) {
  return <div key={index} className={`min-w-0 text-[12.5px] leading-[1.6] wrap-anywhere whitespace-pre-wrap ${index === 0 ? "text-brand-600 font-medium" : "text-neutral-700"}`}>{celula}</div>;
}
function _temp(cabecalho) {
  return <div key={cabecalho}>{cabecalho}</div>;
}
export function blocosTabelaCertificado(tabela: TabelaCertificadoInput): BlocoDocumento[] {
  return tabela.linhas.map((linha, indice) => ({
    id: `${tabela.id}-${linha.id}`,
    render: ({
      primeiroDaPagina
    }) => <FatiaTabela {...tabela} indice={indice} primeiroDaPagina={primeiroDaPagina} />
  }));
}
function FatiaConversa(t0) {
  const $ = _c(51);
  const {
    conversa,
    mensagem,
    parte,
    primeira,
    ultima,
    primeiroDaPagina,
    continuacao,
    mostrarData
  } = t0;
  const recebida = mensagem.direcao === "INBOUND";
  const abre = primeira || primeiroDaPagina;
  const t1 = `px-[37px] ${ultima ? "pb-[26px]" : ""}`;
  const t2 = `overflow-hidden border-x border-neutral-200 ${abre ? "rounded-t-[10px] border-t" : ""} ${ultima ? "rounded-b-[10px] border-b" : ""}`;
  let t3;
  if ($[0] !== abre || $[1] !== conversa || $[2] !== primeira) {
    t3 = abre && <div className="flex items-center gap-3 border-b border-neutral-200 bg-neutral-50 px-4 py-3"><div className="bg-brand-600/10 text-brand-600 flex size-9 shrink-0 items-center justify-center rounded-full"><Phone className="size-4" /></div><div><p className="text-[13px] font-semibold text-neutral-800">{conversa.numero ? formatTelefoneComCodigoPais(conversa.numero) : "N\xFAmero n\xE3o informado"}</p><p className="text-[11px] text-neutral-500">WhatsApp · {conversa.totalDeMensagens}{" "}{conversa.totalDeMensagens === 1 ? "mensagem" : "mensagens"}{!primeira ? " \xB7 continua\xE7\xE3o da conversa" : ""}</p></div></div>;
    $[0] = abre;
    $[1] = conversa;
    $[2] = primeira;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = `flex flex-col px-4 ${abre ? "pt-4" : "pt-1.5"} ${ultima ? "pb-4" : "pb-1.5"} bg-[var(--bg-whatsapp-chat)]`;
  let t5;
  if ($[4] !== mensagem.dataHora || $[5] !== mostrarData) {
    t5 = mostrarData && <div className="mb-3 flex justify-center"><span className="rounded-[8px] bg-white/80 px-3 py-1 text-[11px] text-neutral-500">{dataDoMarco(mensagem.dataHora)}</span></div>;
    $[4] = mensagem.dataHora;
    $[5] = mostrarData;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = `flex ${recebida ? "justify-start" : "justify-end"}`;
  const t7 = `flex max-w-[85%] flex-col rounded-[8px] px-3 py-2 shadow-sm ${recebida ? "bg-white" : "bg-success-50"}`;
  let t8;
  if ($[7] !== continuacao || $[8] !== mensagem.autoriaRotulo) {
    t8 = !continuacao && <p className="text-brand-700 mb-1 text-[11px] font-semibold">{mensagem.autoriaRotulo}</p>;
    $[7] = continuacao;
    $[8] = mensagem.autoriaRotulo;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== continuacao) {
    t9 = continuacao && <p className="mb-1 text-[10px] text-neutral-500">Continuação da mensagem</p>;
    $[10] = continuacao;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== parte) {
    t10 = <p data-testid="certificado-mensagem-texto" className="text-[12.5px] leading-[1.6] wrap-anywhere whitespace-pre-wrap text-neutral-900">{parte}</p>;
    $[12] = parte;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== continuacao || $[15] !== mensagem.midia) {
    t11 = mensagem.midia && !continuacao && <p className="text-brand-700 mt-2 flex items-center gap-2 text-[12px]"><Paperclip className="size-4" />{mensagem.midia.rotulo}{mensagem.midia.referencia ? ` · ${mensagem.midia.referencia}` : ""}</p>;
    $[14] = continuacao;
    $[15] = mensagem.midia;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== mensagem.dataHora) {
    t12 = horaDoMarco(mensagem.dataHora);
    $[17] = mensagem.dataHora;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t12) {
    t13 = <span className="text-[10.5px] text-neutral-500">{t12}</span>;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== mensagem.status || $[22] !== recebida) {
    t14 = !recebida && <OutboundStatusIcon status={mensagem.status} onLight={true} size={14} />;
    $[21] = mensagem.status;
    $[22] = recebida;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t13 || $[25] !== t14) {
    t15 = <div className="mt-1 flex items-center justify-end gap-1">{t13}{t14}</div>;
    $[24] = t13;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== mensagem.status) {
    t16 = mensagem.status === "FAILED" && <p className="text-error-600 mt-1 text-[11px]">Falha no envio · sem comprovação de entrega</p>;
    $[27] = mensagem.status;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t15 || $[32] !== t16 || $[33] !== t7 || $[34] !== t8 || $[35] !== t9) {
    t17 = <div className={t7}>{t8}{t9}{t10}{t11}{t15}{t16}</div>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t15;
    $[32] = t16;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t17 || $[38] !== t6) {
    t18 = <div className={t6}>{t17}</div>;
    $[37] = t17;
    $[38] = t6;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t18 || $[41] !== t4 || $[42] !== t5) {
    t19 = <div className={t4}>{t5}{t18}</div>;
    $[40] = t18;
    $[41] = t4;
    $[42] = t5;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t19 || $[45] !== t2 || $[46] !== t3) {
    t20 = <div className={t2}>{t3}{t19}</div>;
    $[44] = t19;
    $[45] = t2;
    $[46] = t3;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t1 || $[49] !== t20) {
    t21 = <div className={t1}>{t20}</div>;
    $[48] = t1;
    $[49] = t20;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  return t21;
}

/**
 * "Conversa N" e "K mensagens" vêm do bloco congelado, e não do que já chegou: um bloco que
 * atravessa páginas mostra o total dele desde a primeira. O fim do bloco (borda de baixo) só
 * fecha quando a última mensagem dele chegou.
 */
export function blocosConversasCertificado(conversas: ConversaDoCertificado[], secaoCompleta: boolean): BlocoDocumento[] {
  return conversas.flatMap(conversa => {
    const blocoInteiro = secaoCompleta || conversa.mensagens.length >= conversa.totalDeMensagens;
    const fatias = conversa.mensagens.flatMap((mensagem, indiceMensagem) => dividirTextoCertificado(mensagem.textoExibido, FATIA_DO_BALAO).map((parte, indiceParte) => ({
      mensagem,
      parte,
      continuacao: indiceParte > 0,
      mostrarData: indiceParte === 0 && (indiceMensagem === 0 || dataDoMarco(conversa.mensagens[indiceMensagem - 1].dataHora) !== dataDoMarco(mensagem.dataHora))
    })));
    return [{
      id: `conversa-titulo-${conversa.indice}`,
      manterComProximo: true,
      node: <div className="px-[37px] pt-2 pb-4">
            <div className="text-brand-600 mb-2 flex items-center gap-2">
              <MessageCircle className="size-4" />
              <h3 className="text-[12px] font-bold tracking-[0.6px]">Conversa {conversa.indice}</h3>
            </div>
            <div className="h-px bg-neutral-200" />
          </div>
    }, ...fatias.map((fatia, indice): BlocoDocumento => ({
      id: `conversa-${conversa.indice}-fatia-${indice}`,
      render: ({
        primeiroDaPagina
      }) => <FatiaConversa {...fatia} conversa={conversa} primeira={indice === 0} ultima={blocoInteiro && indice === fatias.length - 1} primeiroDaPagina={primeiroDaPagina} />
    }))];
  });
}